import React from 'react';
import { ResponsiveECharts } from './ResponsiveECharts';
import { ChartCard } from '../ChartCard';
import '../studysyncEChartsTheme'; // side effect: registers the 'studysync' theme before render
import { tokens } from '../tokens';
import { ECHARTS_ANIM, markStagger } from '../motion';
import type { ChartSpec, ChartData } from '../copilotTypes';

type PolarBarSpec = Extract<ChartSpec, { chart: 'polar_bar' }>;
type PolarBarData = Extract<ChartData, { chart: 'polar_bar' }>;

export function buildPolarBarOption(_spec: PolarBarSpec, data: PolarBarData) {
  const labels = data.buckets.map((b) => b.label);
  const values = data.buckets.map((b) => b.value);

  return {
    ...ECHARTS_ANIM,
    tooltip: {
      // Two-line tooltip mirrors the histogram's / calendar's: a header line naming the bucket (the
      // day-of-week label), then a bold measured value with the metric unit. Keeps tooltips consistent
      // across every Copilot chart. trigger:'item' so hovering a wedge reports just that day.
      trigger: 'item',
      formatter: (p: any) =>
        `${p.name}<br/><strong>${Math.round(Number(p.value) || 0).toLocaleString()}</strong> ${data.unit}`,
    },
    // The polar grid is centered with a small top inset so the Mon wedge (startAngle 90 -> top) and
    // the radius-axis tick labels both clear the card's title without crowding.
    polar: { center: ['50%', '52%'] },
    // startAngle 90 puts the first category (Mon) at the top and lays the rest out clockwise, so the
    // week reads Mon -> Sun the way the data is ordered.
    angleAxis: {
      type: 'category',
      data: labels,
      startAngle: 90,
      axisLine: { lineStyle: { color: tokens.surface.border } },
      axisTick: { show: false },
      axisLabel: { color: tokens.text.primary, fontFamily: tokens.fontFamily, fontSize: 12, fontWeight: 600 },
    },
    radiusAxis: {
      axisLine: { show: false },
      axisTick: { show: false },
      // ponytail: the numeric ring labels render up the 90° spoke (startAngle), where they pile onto the
      // top wedges and collide with the Mon/Sun day labels — illegible (see the >1M values stacked under
      // "Sun"). For a 7-way day-of-week comparison the wedge LENGTH carries the comparison and the tooltip
      // carries the exact value, so the radial scale is shown as faint unlabeled rings only. Add compact
      // halo'd labels back only if a numeric scale is explicitly needed.
      splitNumber: 4,
      axisLabel: { show: false },
      splitLine: { lineStyle: { color: tokens.surface.grid } },
    },
    series: [
      {
        type: 'bar',
        coordinateSystem: 'polar',
        // Round the outer end of each wedge so the bars read as soft petals rather than hard blocks.
        roundCap: true,
        data: values,
        // Wedges grow in one at a time around the dial; `n` is the bucket count captured here (7 days,
        // so always sparse), never read from the ECharts callback.
        animationDelay: markStagger(values.length),
      },
    ],
  };
}

// memoized so a streaming re-render doesn't rebuild the option -> setOption(notMerge) reflow (see Histogram).
export const PolarBar = React.memo(function PolarBar({ title, spec, data }: { title: string; spec: PolarBarSpec; data: PolarBarData }) {
  const option = React.useMemo(() => buildPolarBarOption(spec, data), [spec, data]);
  return (
    <ChartCard title={title} downloadable>
      <ResponsiveECharts theme="studysync" option={option} style={{ height: 280 }} notMerge />
    </ChartCard>
  );
});
